import { z } from 'zod'

import { httpClientBanking } from '~/domains/banking/shared/infra/http-client-banking'
import { useInfiniteQueryRequest } from '~/ui/hooks/utils'

import { CursorSchema, EarlyRedemptionConditionSchema } from './definitions'

const MarketRedemptionAvailableProductSchema = z.object({
  id: z.string(),
  name: z.string(),
  apy: z.string(),
  maturityDate: z.string(),
  availableAmount: z.number(),
  earlyRedemptionConditions: z.array(EarlyRedemptionConditionSchema).nullable()
})

export type MarketRedemptionAvailableProduct = z.infer<typeof MarketRedemptionAvailableProductSchema>

const MarketRedemptionAvailableProductsResponseSchema = z.object({
  data: z.array(MarketRedemptionAvailableProductSchema),
  cursor: CursorSchema
})

export type MarketRedemptionAvailableProductsResponse = z.infer<typeof MarketRedemptionAvailableProductsResponseSchema>

async function fetchMarketRedemptionAvailableProducts({
  accountId,
  cursor,
  limit = 100
}: {
  accountId: string
  cursor?: string
  limit?: number
}) {
  const { data } = await httpClientBanking({
    url: `/investments/accounts/${accountId}/storefront/redemption_available_products`,
    method: 'GET',
    params: { cursor, limit }
  })

  const parsedData = MarketRedemptionAvailableProductsResponseSchema.parse(data)

  return parsedData
}

export function getUseFetchMarketRedemptionAvailableProductsKey(accountId: string, limit: number) {
  return ['useFetchMarketRedemptionAvailableProducts', accountId, limit] as const
}

export function useFetchMarketRedemptionAvailableProducts({
  accountId,
  limit = 100
}: {
  accountId: string
  limit?: number
}) {
  const { data, ...rest } = useInfiniteQueryRequest<MarketRedemptionAvailableProductsResponse, unknown, true>(
    getUseFetchMarketRedemptionAvailableProductsKey(accountId, limit),
    ({ pageParam }) => fetchMarketRedemptionAvailableProducts({ accountId, cursor: pageParam, limit }),
    {
      getNextPageParam: (lastPage: MarketRedemptionAvailableProductsResponse) => lastPage.cursor.after,
      suspense: true
    }
  )

  return {
    data: data?.pages.flatMap(page => page.data) ?? [],
    ...rest
  }
}
